/*
  Popup (sections/popup.liquid) — ampliado para Grupostop:
  - Programación opcional por fecha/hora de inicio y fin, en hora de Madrid
    (Intl, el horario de verano se aplica solo). Sin inicio o sin fin = sin límite.
  - Identificador de campaña: clave propia de "ya visto" por campaña. Vacío = la
    clave original compartida 'sleektheme:popup'.
  - Exclusión por rutas (prefijo de URL).
  - Retraso (segundos) y scroll (%) configurables.
  - No volver a mostrar tras pulsar el botón (hasta el fin de la campaña o 30 días).
  - Eventos en dataLayer (GTM/GA4): popup_view, popup_click, popup_close.
  - Cuenta atrás hasta la fecha de fin (bloque "Cuenta atrás").
*/
if (!customElements.get('subscription-popup')) {
  customElements.define(
    'subscription-popup',
    class SubscriptionPopup extends DrawerComponent {
      static get observedAttributes() {
        return super.observedAttributes.concat(['data-trigger-open', 'data-repeat-open']);
      }

      constructor() {
        super();
      }

      get requiresBodyAppended() {
        return false;
      }

      connectedCallback() {
        super.connectedCallback();

        this.triggerOpen = this.dataset.triggerOpen;
        this.repeatOpen = this.dataset.repeatOpen;

        this.campaign = (this.dataset.campaign || '').trim();
        this.cookieName = this.campaign ? 'sleektheme:popup:' + this.campaign : 'sleektheme:popup';
        this.clickKey = this.cookieName + ':clicked';

        this.startStr = this.validDateStr(this.dataset.start);
        this.endStr = this.validDateStr(this.dataset.end);
        this.endEpoch = this.endStr ? this.madridToEpoch(this.endStr) : null;
        this.excludes = (this.dataset.exclude || '')
          .split('|')
          .map((s) => s.trim())
          .filter(Boolean);
        this.trackEnabled = this.dataset.track === 'true';
        this.clickBlock = this.dataset.clickBlock === 'true';
        this.delaySeconds = parseFloat(this.dataset.delay) || 5;
        this.scrollPercent = parseFloat(this.dataset.scrollPct) || 25;

        window.FoxTheme.DOMready(this.handleOpen.bind(this));

        window.FoxTheme.utils.addEventDelegate({
          context: this,
          selector: '.btn-apply-coupon-code',
          handler: async (e, target) => {
            e.preventDefault();
            const code = target.dataset.code;
            if (code) this.copyToClipboard(code, target);
          },
        });

        this.addEventListener('click', this.onPopupClick.bind(this), { signal: this.abortController.signal });

        this.initCountdown();
      }

      disconnectedCallback() {
        super.disconnectedCallback();
        if (this.cdTimer) clearInterval(this.cdTimer);
      }

      // ---------- Hora de Madrid ----------
      madridParts(date) {
        const parts = new Intl.DateTimeFormat('en-GB', {
          timeZone: 'Europe/Madrid',
          year: 'numeric',
          month: '2-digit',
          day: '2-digit',
          hour: '2-digit',
          minute: '2-digit',
          hourCycle: 'h23',
        }).formatToParts(date);
        const get = (type) => (parts.find((p) => p.type === type) || {}).value;
        const hour = get('hour') === '24' ? '00' : get('hour');
        return { y: get('year'), mo: get('month'), d: get('day'), h: hour, mi: get('minute') };
      }

      madridNowStr() {
        const p = this.madridParts(new Date());
        return p.y + '-' + p.mo + '-' + p.d + ' ' + p.h + ':' + p.mi;
      }

      // "AAAA-MM-DD HH:MM" en hora de Madrid -> milisegundos UTC
      madridToEpoch(str) {
        const m = str.match(/^(\d{4})-(\d{2})-(\d{2}) (\d{2}):(\d{2})$/);
        if (!m) return null;
        const guess = Date.UTC(+m[1], +m[2] - 1, +m[3], +m[4], +m[5]);
        const p = this.madridParts(new Date(guess));
        const asUTC = Date.UTC(+p.y, +p.mo - 1, +p.d, +p.h, +p.mi);
        return guess - (asUTC - guess);
      }

      validDateStr(value) {
        const s = (value || '').trim();
        if (!s) return null;
        if (/^\d{4}-\d{2}-\d{2} \d{2}:\d{2}$/.test(s)) return s;
        console.warn('[popup] Fecha no válida (formato AAAA-MM-DD y HH:MM), se ignora:', s);
        return null;
      }

      // ---------- Reglas de visualización ----------
      isEligible() {
        const now = this.madridNowStr();
        if (this.startStr && now < this.startStr) return false;
        if (this.endStr && now > this.endStr) return false;

        if (this.excludes.length) {
          const path = window.location.pathname;
          const excluded = this.excludes.some((rule) => {
            const clean = rule.replace(/^https?:\/\/[^/]+/i, '');
            return clean && path.indexOf(clean) === 0;
          });
          if (excluded) return false;
        }
        return true;
      }

      shouleBeShow() {
        if (Shopify.designMode) return true;
        return this.isEligible();
      }

      hideTeaser() {
        document
          .querySelectorAll('.subscription-popup__teaser[aria-controls="' + this.id + '"]')
          .forEach((btn) => {
            btn.hidden = true;
          });
      }

      prepareToShow() {
        super.prepareToShow();
        this.setRepeatOpen();
        if (!Shopify.designMode) this.track('popup_view');
      }

      hide() {
        const wasOpen = this.open;
        const result = super.hide();
        if (wasOpen && !Shopify.designMode) this.track('popup_close');
        return result;
      }

      handleOpen() {
        if (Shopify.designMode) return;

        if (!this.isEligible()) {
          this.hideTeaser();
          return false;
        }

        let savedData = FoxTheme.utils.getStorage(this.cookieName);
        if (savedData && savedData.opened) {
          return false;
        }

        if (this.clickBlock && FoxTheme.utils.getStorage(this.clickKey)) {
          return false;
        }

        switch (this.triggerOpen) {
          case 'delay':
            setTimeout(() => {
              this.show();
            }, this.delaySeconds * 1000);
            break;
          case 'scroll_down':
            this.triggerAfterScroll(this.scrollPercent, this.show.bind(this));
            break;
          default:
            this.show();
            break;
        }
      }

      // Abre cuando se ha recorrido `percent` % de la página
      triggerAfterScroll(percent, trigger) {
        let opened = false;
        const onScroll = () => {
          if (opened) return;
          const doc = document.documentElement;
          const max = Math.max(1, (doc.scrollHeight || document.body.scrollHeight) - window.innerHeight);
          const scrollTop = window.pageYOffset || doc.scrollTop || 0;
          if ((scrollTop / max) * 100 >= percent) {
            opened = true;
            window.removeEventListener('scroll', onScroll);
            trigger();
          }
        };
        window.addEventListener('scroll', onScroll, { passive: true });
      }

      // ---------- Clics y medición ----------
      onPopupClick(e) {
        if (Shopify.designMode) return;

        const cta = e.target.closest('.subscription-popup__cta a');
        if (cta) {
          this.track('popup_click', { popup_click_type: 'cta', popup_click_url: cta.getAttribute('href') || '' });
          if (this.clickBlock) {
            let days = 30;
            if (this.endEpoch) days = Math.max(1, Math.ceil((this.endEpoch - Date.now()) / 86400000) + 1);
            FoxTheme.utils.setStorage(this.clickKey, { clicked: true }, days);
          }
          return;
        }

        if (e.target.closest('.btn-apply-coupon-code')) {
          this.track('popup_click', { popup_click_type: 'code' });
        }
      }

      track(eventName, extra) {
        if (!this.trackEnabled) return;
        try {
          window.dataLayer = window.dataLayer || [];
          window.dataLayer.push(
            Object.assign({ event: eventName, popup_campaign: this.campaign || 'sin-campana', popup_id: this.id }, extra || {})
          );
        } catch (err) {}
      }

      // ---------- Cuenta atrás ----------
      initCountdown() {
        const box = this.querySelector('[data-popup-countdown]');
        if (!box || !this.endEpoch) return;

        const els = {
          d: box.querySelector('[data-unit="d"]'),
          h: box.querySelector('[data-unit="h"]'),
          m: box.querySelector('[data-unit="m"]'),
          s: box.querySelector('[data-unit="s"]'),
        };
        const pad = (n) => (n < 10 ? '0' : '') + n;
        const tick = () => {
          const diff = this.endEpoch - Date.now();
          if (diff <= 0) {
            box.hidden = true;
            if (this.cdTimer) clearInterval(this.cdTimer);
            return;
          }
          const t = Math.floor(diff / 1000);
          if (els.d) els.d.textContent = pad(Math.floor(t / 86400));
          if (els.h) els.h.textContent = pad(Math.floor((t % 86400) / 3600));
          if (els.m) els.m.textContent = pad(Math.floor((t % 3600) / 60));
          if (els.s) els.s.textContent = pad(t % 60);
        };

        box.hidden = false;
        tick();
        this.cdTimer = setInterval(tick, 1000);
      }

      copyToClipboard(code, button) {
        navigator.clipboard.writeText(code);
        button.innerText = button.dataset.copiedText;
      }

      setRepeatOpen() {
        if (Shopify.designMode) return;

        let expires;
        switch (this.repeatOpen) {
          case 'no_repeat':
            expires = 365;
            break;
          case 'every_3_mins':
            expires = 3 / 60 / 24;
            break;
          case 'every_5_mins':
            expires = 5 / 60 / 24;
            break;
          case 'every_10_mins':
            expires = 1 / 6 / 24;
            break;
          case 'every_15_mins':
            expires = 15 / 60 / 24;
            break;
          case 'every_30_mins':
            expires = 1 / 2 / 24;
            break;
          case 'every_1_hr':
            expires = 1 / 24;
            break;
          case 'every_6_hrs':
            expires = 6 / 24;
            break;
          case 'every_12_hrs':
            expires = 1 / 2;
            break;
          case 'every_day':
            expires = 1;
            break;
          case 'every_3_days':
            expires = 3;
            break;
          case 'every_week':
            expires = 7;
            break;
          case 'every_2_weeks':
            expires = 14;
            break;
          case 'every_month':
            expires = 30;
            break;
          default:
            expires = 7;
            break;
        }

        window.FoxTheme.utils.setStorage(this.cookieName, { opened: true }, expires);
      }
    }
  );
}